// Course curriculum — flat lesson structure (one content item per lesson)
// contentType: "slide" | "video" | "framework" | "none"
import { Sparkles } from "lucide-react";

const KEY_PREFIX = "ai-employees";
// Marketing thumbnail only — not gated content, safe to keep on a public CDN URL.
const PUBLIC_CDN = "https://pub-e4209e8384fe4db983eaa5f05c20e56b.r2.dev/ai-employees";
const THUMB = `${PUBLIC_CDN}/thumbnails/ai-employees-masterclass-thumbnail.png`;

export const COURSE = {
  title: "AI Employees Course",
  modules: [
    // ── FOUNDATIONS ──────────────────────────────────────────────────────────
    {
      id: "m01a",
      title: "Welcome",
      section: "Foundations",
      duration: "3 min",
      contentType: "slide",
      key: `${KEY_PREFIX}/pdfs/welcome-ai-employees-masterclass-ai-agents-for-business-growth.png`,
    },
    {
      id: "m01b",
      title: "The Founder",
      section: "Foundations",
      duration: "1 min",
      contentType: "slide",
      key: `${KEY_PREFIX}/pdfs/founder-ai-agents-for-business-owners-scale-your-business-with-ai-favor-studio.png`,
    },
    {
      id: "m01c",
      title: "Real Systems. Real Results.",
      section: "Foundations",
      duration: "1 min",
      contentType: "slide",
      key: `${KEY_PREFIX}/pdfs/past-ai-client-acquisition-systems-favor-studio.png`,
    },
    {
      id: "m01d",
      title: "Course Roadmap",
      section: "Foundations",
      duration: "1 min",
      contentType: "slide",
      key: `${KEY_PREFIX}/pdfs/ai-agents-for-business-table-of-contents.png`,
    },
    {
      id: "m01e",
      title: "What You'll Learn",
      section: "Foundations",
      duration: "1 min",
      contentType: "slide",
      key: `${KEY_PREFIX}/pdfs/what-you'll-learn-ai-agents-course-favor-studio.png`,
    },
    {
      id: "m02",
      title: "What Is An AI Employee",
      section: "Foundations",
      duration: "2 min",
      contentType: "slide",
      key: `${KEY_PREFIX}/pdfs/what-is-an-ai-agent-business-guide-favor-studio.png`,
    },
    {
      id: "m03a",
      title: "AI Agent vs AI Workflow",
      section: "Foundations",
      duration: "5 min",
      contentType: "slide",
      key: `${KEY_PREFIX}/pdfs/ai-agent-vs-ai-workflow-explained-favor-studio.png`,
    },
    {
      id: "m03b",
      title: "The AI-Assisted Coding Method",
      section: "Foundations",
      duration: "1 min",
      contentType: "slide",
      key: `${KEY_PREFIX}/pdfs/ai-assisted-coding-method-favor-studio.png`,
    },
    {
      id: "m03c",
      title: "Managing Long AI Builds",
      section: "Foundations",
      duration: "1 min",
      contentType: "slide",
      key: `${KEY_PREFIX}/pdfs/managing-long-ai-builds-favor-studio.png`,
    },

    // ── STRATEGY ─────────────────────────────────────────────────────────────
    {
      id: "m04",
      title: "Identify Your Business Constraint",
      section: "Strategy",
      duration: "2 min",
      contentType: "slide",
      key: `${KEY_PREFIX}/pdfs/identify-your-business-constraint-favor-studio.png`,
    },
    {
      id: "m05",
      title: "The Briefing Framework",
      section: "Strategy",
      duration: "7 min",
      contentType: "framework",
      key: `${KEY_PREFIX}/frameworks/favor-studio-ai-agent-briefing-framework-resource.pdf`,
      description:
        "Structure your AI agent project before you build — avoids 90% of common build errors.",
    },

    // ── SETUP ────────────────────────────────────────────────────────────────
    {
      id: "m06a",
      title: "Tools & Setup Checklist",
      section: "Setup",
      duration: "3 min",
      contentType: "slide",
      key: `${KEY_PREFIX}/pdfs/ai-agent-tools-and-setup-checklist-favor-studio.png`,
    },
    {
      id: "m06b",
      title: "Tools & Setup Checklist — Framework",
      section: "Setup",
      duration: "1 min",
      contentType: "framework",
      key: `${KEY_PREFIX}/frameworks/favor-studio-tools-and-setup-checklist-resource.pdf`,
      description:
        "Your complete checklist of tools, accounts, and settings before starting any build.",
    },
    {
      id: "m07a",
      title: "Security & Best Practices",
      section: "Setup",
      duration: "3 min",
      contentType: "slide",
      key: `${KEY_PREFIX}/pdfs/api-key-security-protocols-favor-studio.png`,
    },
    {
      id: "m07b",
      title: "Security & Best Practices — Framework",
      section: "Setup",
      duration: "1 min",
      contentType: "framework",
      key: `${KEY_PREFIX}/frameworks/favor-studio-security-and-best-practices-resource.pdf`,
      description:
        "Protect your API keys and production systems. Complete before deploying any agent.",
    },

    // ── BUILD ────────────────────────────────────────────────────────────────
    {
      id: "m08a",
      title: "Stage 1 — Setup & Define",
      section: "Build",
      duration: "8 min",
      contentType: "video",
      key: `${KEY_PREFIX}/videos/step-01-setup-and-define.mp4`,
      poster: THUMB,
    },
    {
      id: "m08b",
      title: "Stage 2 — Setting Up Your Terminal",
      section: "Build",
      duration: "8 min",
      contentType: "video",
      key: `${KEY_PREFIX}/videos/step-02-setting-up-your-terminal.mp4`,
      poster: THUMB,
    },
    {
      id: "m09",
      title: "Stage 3 — Build the Logic",
      section: "Build",
      duration: "14 min",
      contentType: "video",
      key: `${KEY_PREFIX}/videos/step-03-building-the-agents-logic.mp4`,
      poster: THUMB,
    },

    // ── DEBUG YOUR AI EMPLOYEE ────────────────────────────────────────────────
    {
      id: "m10a",
      title: "Step 1 — Open Inspect",
      section: "Debug Your AI Employee",
      duration: "3 min",
      contentType: "slide",
      key: `${KEY_PREFIX}/pdfs/debug-step-01-open-inspect.JPG`,
    },
    {
      id: "m10b",
      title: "Step 2 — Developer Panel Opens",
      section: "Debug Your AI Employee",
      duration: "1 min",
      contentType: "slide",
      key: `${KEY_PREFIX}/pdfs/debug-step-02-developer-panel.JPG`,
    },
    {
      id: "m10c",
      title: "Step 3 — Take a Screenshot",
      section: "Debug Your AI Employee",
      duration: "1 min",
      contentType: "slide",
      key: `${KEY_PREFIX}/pdfs/debug-step-03-console-and-issues.JPG`,
    },
    {
      id: "m10d",
      title: "Step 4 — Send to Claude",
      section: "Debug Your AI Employee",
      duration: "1 min",
      contentType: "slide",
      key: `${KEY_PREFIX}/pdfs/debug-step-04-send-to-claude.JPG`,
    },

    // ── DEPLOY ────────────────────────────────────────────────────────────────
    {
      id: "m11",
      title: "Stage 4 — Deploy Employee",
      section: "Deploy",
      duration: "8 min",
      contentType: "video",
      key: `${KEY_PREFIX}/videos/step-04-deploy-to-cloudflare.mp4`,
      poster: THUMB,
    },
    {
      id: "m12",
      title: "Agent Dashboard",
      section: "Deploy",
      duration: "13 min",
      contentType: "video",
      key: `${KEY_PREFIX}/videos/step-05-ai-agent-dashboard.mp4`,
      poster: THUMB,
    },

    // ── SCALE ────────────────────────────────────────────────────────────────
    {
      id: "m13",
      title: "Next Steps — Scale Your Systems",
      section: "Scale",
      duration: "5 min",
      contentType: "none",
    },
  ],

  bonus: {
    id: "b01",
    title: "Productize Your AI Employee",
    duration: "10 min",
    section: "Bonus",
    contentType: "framework",
    key: `${KEY_PREFIX}/frameworks/favor-studio-productize-playbook-resource.pdf`,
    description:
      "Turn your AI build skills into a productized offer you can sell to clients.",
  },

  resources: [
    {
      id: "r01",
      title: "The Briefing Framework — PDF",
      type: "PDF",
      key: `${KEY_PREFIX}/frameworks/favor-studio-ai-agent-briefing-framework-resource.pdf`,
      description: "Structure your AI agent project before you build.",
    },
    {
      id: "r02",
      title: "Tools & Setup Checklist — PDF",
      type: "PDF",
      key: `${KEY_PREFIX}/frameworks/favor-studio-tools-and-setup-checklist-resource.pdf`,
      description: "Every tool and account you need before your first build.",
    },
    {
      id: "r03",
      title: "Security & Best Practices — PDF",
      type: "PDF",
      key: `${KEY_PREFIX}/frameworks/favor-studio-security-and-best-practices-resource.pdf`,
      description: "Protect your API keys and production systems.",
    },
    {
      id: "r04",
      title: "Dashboard Framework — PDF",
      type: "PDF",
      key: `${KEY_PREFIX}/frameworks/favor-studio-dashboard-framework-prompt-resource.pdf`,
      description: "The prompt system behind a production-grade agent dashboard.",
    },
    {
      id: "r05",
      title: "Agent Briefing Templates — PDF",
      type: "PDF",
      key: `${KEY_PREFIX}/frameworks/favor-studio-ai-agent-template-prompts-resource.pdf`,
      description: "Ready-to-use prompt templates for your AI agent builds.",
    },
  ],
};

export const ICON = Sparkles;
